import { useState, useEffect } from "react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import PageHeading from "../../../PageHeading";
import TableSkeleton from "../../../TableSkeleton";
import DynamicSelect from "../../../DynamicSelect";
import AccountExportOptions from "../../../AccountExportOptions";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const OfficeAccountSummary = () => {
  const [data, setData] = useState([]);
  const [grandTotals, setGrandTotals] = useState({ deposits: 0, expenses: 0 });
  const [openingBalance, setOpeningBalance] = useState(0);
  const [todayStats, setTodayStats] = useState({ income: 0, expense: 0 });
  const [loading, setLoading] = useState(true);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Filter States
  const [filterType, setFilterType] = useState("monthly");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString(),
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const API_KEY =
    "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const SUMMARY_URL =
    "https://api.muktihospital.com/api/hospital/office-account-summary";

  const formatApiDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB");
  };

const fetchSummary = async () => {
    setLoading(true);
    try {
      const params = { type: filterType, limit: "0" };

      if (filterType === "daily") {
        params.startDate = formatApiDate(startDate);
        params.endDate = formatApiDate(endDate);
      } else if (filterType === "monthly") {
        const [y, m] = selectedMonth.split("-");
        params.year = y;
        params.month = m;
      } else if (filterType === "yearly") {
        params.year = selectedYear;
      }

      const response = await axios.get(SUMMARY_URL, {
        headers: { "x-api-key": API_KEY },
        params: params,
      });

      if (response.data) {
        setData(response.data.summary || []);
        setGrandTotals({
          deposits: response.data.grandTotalDeposits || 0,
          expenses: response.data.grandTotalExpenses || 0,
        });
        setOpeningBalance(response.data.openingBalance || 0);
        setTodayStats({ 
            income: response.data.todayIncome || 0, 
            expense: response.data.todayExpense || 0 
        });
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
      toast.error("Failed to load summary data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    setCurrentPage(1);
  }, [filterType, startDate, endDate, selectedMonth, selectedYear]);

  // --- CALCULATION LOGIC ---
  const currentPeriodNet = grandTotals.deposits - grandTotals.expenses;
  const closingBalance = openingBalance + currentPeriodNet;

  // --- DATA LISTS ---
  const incomeList = data.filter((item) => item.deposit > 0);
  const expenseList = data.filter((item) => item.expense > 0);

  // --- PAGINATION LOGIC ---
  const totalIncomePages = Math.ceil(incomeList.length / itemsPerPage);
  const totalExpensePages = Math.ceil(expenseList.length / itemsPerPage);
  const totalPages = Math.max(totalIncomePages, totalExpensePages) || 1;

  const currentIncomeData = incomeList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const currentExpenseData = expenseList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) pages.push(1, 2, 3, 4, 5, "...", totalPages);
      else if (currentPage >= totalPages - 3)
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        );
      else
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages,
        );
    }
    return pages.map((p, index) => (
      <li key={index}>
        {p === "..." ? (
          <span className="px-3 py-2 text-gray-500">...</span>
        ) : (
          <button
            onClick={() => handlePageChange(p)}
            className={`table-pagination-item ${currentPage === p ? "bg-M-primary-color text-white" : ""}`}
          >
            {p}
          </button>
        )}
      </li>
    ));
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Hospital Office" },
    { label: "Account Summary" },
  ];
  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];
  const headers = ["Sl", "Date", "Description", "Deposit", "Expense"];
  const dataMapper = (row) =>
    [
      data.indexOf(row) + 1,
      formatDateDisplay(row.date),
      row.description,
      row.deposit || 0,
      row.expense || 0,
    ].join("\t");

    const officeSummaryText = `Today's Income: BDT ${todayStats.income.toLocaleString()}  |  Today's Expense: BDT ${todayStats.expense.toLocaleString()}  |  Opening: BDT ${openingBalance.toLocaleString()}\n` +
                          `Current Income: BDT ${grandTotals.deposits.toLocaleString()}  |  Current Expense: BDT ${grandTotals.expenses.toLocaleString()}  |  Closing: BDT ${closingBalance.toLocaleString()}`;

  return (
    <div>
      <ToastContainer />
      <div className="print-hidden">
        <PageHeading title="Account Summary" breadcrumbs={breadcrumbs} />
      </div>

      <div className="bg-white shadow-sm rounded-lg">
        {/* --- HEADER --- */}
        <div className="border-b border-dashed border-gray-300 p-4 flex items-center justify-between gap-2 flex-wrap print-hidden">
          <div className="inline-flex items-center gap-3">
            <h2 className="text-base font-medium text-gray-700">
              Office Financial Report
            </h2>
          </div>
          <div className="flex items-center">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Filter:</label>
                <div className="w-32">
                  <DynamicSelect
                    options={filterOptions}
                    value={filterOptions.find((o) => o.value === filterType)}
                    onChange={(o) => setFilterType(o.value)}
                    isSearchable={false}
                  />
                </div>
              </div>

              {filterType === "daily" && (
                <>
                  <div className="flex items-center gap-2">
                    <label className="label mb-0 shrink-0">From:</label>
                    <DatePicker 
                        selected={startDate} 
                        onChange={(date) => setStartDate(date)} 
                        dateFormat="dd MMM yyyy" 
                        className="input-field w-full text-center"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="label mb-0 shrink-0">To:</label>
                    <DatePicker 
                        selected={endDate} 
                        onChange={(date) => setEndDate(date)} 
                        dateFormat="dd MMM yyyy" 
                        className="input-field w-full text-center"
                    />
                  </div>
                </>
              )}
              {filterType === "monthly" && (
                <div className="flex items-center gap-2">
                  <label className="label mb-0 shrink-0">Month:</label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="input-field"
                  />
                </div>
              )}
              {filterType === "yearly" && (
                <div className="flex items-center gap-2">
                  <label className="label mb-0 shrink-0">Year:</label>
                  <input
                    type="number"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="input-field w-24"
                    min="2000"
                    max="2100"
                  />
                </div>
              )}
            </div>
            <AccountExportOptions
                data={data}
                title="Office Financial Report"
                subTitle={officeSummaryText}
                headers={headers}
                dataMapper={dataMapper}
                dropdownOpen={dropdownOpen}
                toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
              />
          </div>
        </div>

        <div className="p-4 print-wrapper">
          {/* --- GRAND TOTAL SUMMARY CARD --- */}
          <div className="bg-gray-100 p-4 rounded-lg mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 border border-gray-200">
            
            {/* 1. Today's Income */}
            <div className="text-center md:text-left border-b sm:border-b-0 pb-2 sm:pb-0 border-gray-300 lg:border-r lg:pr-4">
              <p className="text-xs text-gray-500 uppercase font-bold">Today's Income</p>
              <p className="text-lg font-bold text-green-600">৳ {todayStats.income.toLocaleString()}</p>
            </div>

            {/* 2. Today's Expense */}
            <div className="text-center md:text-left border-b sm:border-b-0 pb-2 sm:pb-0 border-gray-300 lg:border-r lg:pr-4">
              <p className="text-xs text-gray-500 uppercase font-bold">Today's Expense</p>
              <p className="text-lg font-bold text-red-600">৳ {todayStats.expense.toLocaleString()}</p>
            </div>

            {/* 3. Opening Balance */}
            <div className="text-center md:text-left border-b sm:border-b-0 pb-2 sm:pb-0 border-gray-300 lg:border-r lg:pr-4">
              <p className="text-xs text-gray-500 uppercase font-semibold">Opening Bal.</p>
              <p className={`text-lg font-bold ${openingBalance >= 0 ? "text-blue-600" : "text-orange-600"}`}>৳ {openingBalance.toLocaleString()}</p>
            </div>

            {/* 4. Current Income */}
            <div className="text-center md:text-left border-b sm:border-b-0 pb-2 sm:pb-0 border-gray-300 lg:border-r lg:pr-4">
              <p className="text-xs text-gray-500 uppercase font-semibold">Current Income</p>
              <p className="text-lg font-bold text-green-700">৳ {grandTotals.deposits.toLocaleString()}</p>
            </div>

            {/* 5. Current Expense */}
            <div className="text-center md:text-left border-b sm:border-b-0 pb-2 sm:pb-0 border-gray-300 lg:border-r lg:pr-4">
              <p className="text-xs text-gray-500 uppercase font-semibold">Current Expense</p>
              <p className="text-lg font-bold text-red-700">৳ {grandTotals.expenses.toLocaleString()}</p>
            </div>

            {/* 6. Closing Balance */}
            <div className="text-center md:text-right">
              <p className="text-xs text-gray-500 uppercase font-semibold">Closing Bal.</p>
              <p className={`text-xl font-bold ${closingBalance >= 0 ? "text-blue-700" : "text-red-700"}`}>৳ {closingBalance.toLocaleString()}</p>
            </div>
          </div>
          {/* --- TWO COLUMN LAYOUT --- */}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* --- INCOME TABLE --- */}

            <div className="border rounded-lg overflow-hidden flex flex-col h-full">
              <div className="bg-green-50 p-3 border-b border-green-200">
                <h3 className="text-green-800 font-bold text-center uppercase">
                  Details of Income
                </h3>
              </div>

              <div className="flex-grow">
                <table className="w-full divide-y divide-gray-200 print-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left text-xs font-bold text-gray-600 uppercase">
                        Date
                      </th>

                      <th className="p-2 text-left text-xs font-bold text-gray-600 uppercase">
                        Description
                      </th>

                      <th className="p-2 text-right text-xs font-bold text-gray-600 uppercase">
                        Amount
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="3" className="p-4">
                          <TableSkeleton
                            rowCount={5}
                            columns={[{ width: "100%" }]}
                          />
                        </td>
                      </tr>
                    ) : currentIncomeData.length === 0 ? (
                      <tr>
                        <td
                          colSpan="3"
                          className="p-4 text-center text-gray-500 text-sm"
                        >
                          No Income Records
                        </td>
                      </tr>
                    ) : (
                      currentIncomeData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-2 text-xs text-gray-700 whitespace-nowrap">
                            {formatDateDisplay(row.date)}
                          </td>

                          <td className="p-2 text-xs text-gray-700">
                            {row.description}
                          </td>

                          <td className="p-2 text-right text-xs font-bold text-green-600">
                            {row.deposit.toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* --- EXPENSE TABLE --- */}

            <div className="border overflow-hidden rounded-lg flex flex-col h-auto">
              <div className="bg-red-50 p-3 border-b border-red-200">
                <h3 className="text-red-800 font-bold text-center uppercase">
                  Details of Expense
                </h3>
              </div>

              <div className="flex-grow">
                <table className="w-full divide-y divide-gray-200 print-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left text-xs font-bold text-gray-600 uppercase">
                        Date
                      </th>

                      <th className="p-2 text-left text-xs font-bold text-gray-600 uppercase">
                        Description
                      </th>

                      <th className="p-2 text-right text-xs font-bold text-gray-600 uppercase">
                        Amount
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan="3" className="p-4">
                          <TableSkeleton
                            rowCount={5}
                            columns={[{ width: "100%" }]}
                          />
                        </td>
                      </tr>
                    ) : currentExpenseData.length === 0 ? (
                      <tr>
                        <td
                          colSpan="3"
                          className="p-4 text-center text-gray-500 text-sm"
                        >
                          No Expense Records
                        </td>
                      </tr>
                    ) : (
                      currentExpenseData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-2 text-xs text-gray-700 whitespace-nowrap">
                            {formatDateDisplay(row.date)}
                          </td>

                          <td className="p-2 text-xs text-gray-700">
                            {row.description}
                          </td>

                          <td className="p-2 text-right text-xs font-bold text-red-600">
                            {row.expense.toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* --- PAGINATION --- */}
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="flex items-center justify-between gap-2 w-full flex-wrap">
                <p className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </p>
                <nav>
                  <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                    <li>
                      <button
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                        className="table-pagination-item disabled:opacity-50"
                      >
                        <MdKeyboardArrowLeft />
                      </button>
                    </li>
                    {renderPagination()}
                    <li>
                      <button
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                        className="table-pagination-item disabled:opacity-50"
                      >
                        <MdKeyboardArrowRight />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS for Two Column Print Layout */}
      <style>{`
        @media print {
            .print-hidden { display: none !important; }
            .print-wrapper { width: 100%; }
            .border { border: 1px solid #ccc !important; }
        }
      `}</style>
    </div>
  );
};

export default OfficeAccountSummary;
